import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
// eslint-disable-next-line no-restricted-imports
import { Pressable, type TextInput, View } from "react-native"
import { useBottomSheetInternal } from "@gorhom/bottom-sheet"
import {
  type KeyboardTypeOptions,
  type ReturnKeyTypeOptions,
} from "../../../node_modules/react-native/Libraries/Components/TextInput/TextInput"
import PasswordEye from "@/assets/icons/password-eye.svg"
import PasswordEyeInactive from "@/assets/icons/password-eye-inactive.svg"
import { cn } from "@/components/util/cn"
import BFText from "@/components/standard/BFText"
import { BFTextInput } from "@/components/standard/BFTextInput"

type TextContentType =
  | "none"
  | "URL"
  | "addressCity"
  | "addressCityAndState"
  | "addressState"
  | "countryName"
  | "creditCardNumber"
  | "creditCardExpiration"
  | "creditCardExpirationMonth"
  | "creditCardExpirationYear"
  | "creditCardSecurityCode"
  | "creditCardType"
  | "creditCardName"
  | "creditCardGivenName"
  | "creditCardMiddleName"
  | "creditCardFamilyName"
  | "emailAddress"
  | "familyName"
  | "fullStreetAddress"
  | "givenName"
  | "jobTitle"
  | "location"
  | "middleName"
  | "name"
  | "namePrefix"
  | "nameSuffix"
  | "nickname"
  | "organizationName"
  | "postalCode"
  | "streetAddressLine1"
  | "streetAddressLine2"
  | "sublocality"
  | "telephoneNumber"
  | "username"
  | "password"
  | "newPassword"
  | "oneTimeCode"
  | "birthdate"
  | "birthdateDay"
  | "birthdateMonth"
  | "birthdateYear"
  | undefined

export interface FeedbackTextInputProps {
  value?: string
  placeholder?: string
  className?: string
  error?: string | null
  keyboardType?: KeyboardTypeOptions
  returnKeyType?: ReturnKeyTypeOptions
  isSecured?: boolean
  disabled?: boolean
  isBottomSheetInput?: boolean
  onChange?: ((val?: string | null) => void) | Dispatch<SetStateAction<string>>
  autoFocus?: boolean
  textContentType?: TextContentType
  testID?: string
}

export default function FeedbackTextInput(props: FeedbackTextInputProps) {
  const {
    value,
    placeholder,
    className,
    error,
    keyboardType = "default",
    isSecured,
    disabled = false,
    isBottomSheetInput = false,
    returnKeyType = "default",
    onChange,
    autoFocus = false,
    textContentType,
    testID,
  } = props
  const [secure, setSecure] = useState<boolean>(isSecured === true)

  const bottomSheetInternal = useBottomSheetInternal(true)

  const { shouldHandleKeyboardEvents } = bottomSheetInternal || {}

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus()
    }
  }, [autoFocus])

  const inputRef = useRef<TextInput>(null)

  useEffect(() => {
    return () => {
      // Reset the flag on unmount
      if (shouldHandleKeyboardEvents) {
        shouldHandleKeyboardEvents.value = false
      }
    }
  }, [shouldHandleKeyboardEvents])

  const onChangeText = (val: string) => {
    if (onChange) {
      onChange(val)
    }
  }

  const handleOnFocus = useCallback(() => {
    if (shouldHandleKeyboardEvents) {
      shouldHandleKeyboardEvents.value = isBottomSheetInput
    }
  }, [shouldHandleKeyboardEvents, isBottomSheetInput])

  const handleOnBlur = useCallback(() => {
    if (shouldHandleKeyboardEvents) {
      shouldHandleKeyboardEvents.value = isBottomSheetInput
    }
  }, [shouldHandleKeyboardEvents, isBottomSheetInput])

  return (
    <View className="flex flex-col relative">
      <BFTextInput
        testID={testID}
        placeholder={placeholder}
        value={value ?? ""}
        onChangeText={onChangeText}
        autoCapitalize="none"
        autoComplete="off"
        autoCorrect={false}
        keyboardType={keyboardType}
        returnKeyType={returnKeyType}
        secureTextEntry={secure}
        textContentType={textContentType}
        editable={!disabled}
        className={className}
        variant={error ? "formErrorDark" : "formDark"}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        ref={inputRef}
      />
      {isSecured === true && (
        <Pressable
          testID={"view-password-button"}
          className={cn("absolute right-4 top-4", "web:top-3")}
          onPress={() => {
            setSecure(!secure)
          }}
        >
          {secure ? (
            <PasswordEye width={24} height={24} />
          ) : (
            <PasswordEyeInactive width={24} height={24} />
          )}
        </Pressable>
      )}
      {error && (
        <View className="w-full h-[18px]">
          <BFText className="w-full text-salmon text-[10px] leading-3 pt-[6px]">
            {error}
          </BFText>
        </View>
      )}
    </View>
  )
}
